/**
 * Shared types for the DocumentTemplate authoring UI.
 * Mirrors apps/documents + apps/programs serializers.
 *
 * NOTE: the old `Question`/`Answer` framework was unified into
 * `programs.ProgramSpec` / `programs.ProgramObservation`. A spec *is* the
 * interview question (it carries `prompt`, `guidance`, `interview_priority`,
 * `eligible_respondent_roles`) plus the structured-spec fields (`spec_type`,
 * `unit`, `category_options`). These types reflect that model.
 */

export type OwnerLevel = 'system' | 'office' | 'program_manager' | 'program'

export type SpecType =
  | 'numeric'
  | 'boolean'
  | 'categorical'
  | 'ordered_categorical'
  | 'text'
  | 'datetime'

export type SpecCategory =
  | 'timeline'
  | 'acquisition'
  | 'technical'
  | 'operational'
  | 'budget'
  | 'classification'
  | 'problem'
  | 'environment'
  | 'constraint'

/** Interview priority (ProgramSpec.interview_priority). */
export type SpecPriority = 'essential' | 'standard' | 'optional'

export interface CategoryOption {
  value: string
  label: string
}

/** A ProgramSpec (the unified interview question + structured spec). */
export interface Spec {
  id: number
  key: string
  name: string | null
  description: string | null
  spec_type: SpecType | null
  category: SpecCategory | null
  unit: string | null
  category_options: CategoryOption[] | null
  // Interview-question fields
  prompt: string | null
  guidance: string | null
  interview_priority: SpecPriority | null
  eligible_respondent_roles: string[] | null
  // Cascading ownership
  owner_level: OwnerLevel
  owner_office: number | null
  owner_user: number | null
  owner_program: number | null
  parent: number | null
  is_removed: boolean
}

export interface SectionSpecMembership {
  id: number
  section: string
  spec: number
  /** Nested ProgramSpec (read-only; replaces the old `question_detail`). */
  spec_detail: Spec
  order: number
  required: boolean
}

export interface Section {
  id: string
  template: string
  key: string
  title: string
  order: number
  owner_level: OwnerLevel
  owner_office: number | null
  owner_user: number | null
  owner_program: number | null
  parent: string | null
  is_removed: boolean
  description: string | null
  writing_agent_instructions: string | null
  /** Membership rows. Serializer key remains `section_questions`. */
  section_questions: SectionSpecMembership[]
  writing_skill: string | null
  skill_config: Record<string, unknown> | null
  depends_on: string[] | null
  depends_on_all: boolean | null
}

export interface SkillConfigField {
  key: string
  label: string
  type: 'string' | 'integer' | 'select'
  required: boolean
  default: unknown
  description: string
  options: string[]
}

export interface SkillManifest {
  key: string
  label: string
  description: string
  config_schema: SkillConfigField[]
  shows_questions: boolean
}

export interface Sample {
  id: string
  template: string
  title: string
  file_url: string | null
  file_size: number
  content: string
  notes: string
  order: number
  is_active: boolean
  uploaded_by: number | null
  uploaded_by_name: string | null
  uploaded_at: string
  updated_at: string
}

export interface DocumentTemplate {
  id: string
  key: string
  name: string
  version: number
  is_active: boolean
  owner_level: OwnerLevel
  owner_office: number | null
  owner_user: number | null
  owner_program: number | null
  parent: string | null
  previous_version: string | null
  is_removed: boolean
  description: string | null
  writing_agent_instructions: string | null
  sections: Section[]
  samples: Sample[]
}

export interface Scope {
  level: OwnerLevel
  /** The owner FK value for this scope — office id, user id, or program id.
   * Omitted at system scope. */
  ownerId?: number
}

/** Matches the backend's ResolvedQuestion dataclass returned from the
 * /templates/{id}/effective/ endpoint (still keyed `questions` in the JSON).
 * Values cascade up the parent chain; `owner_level` reflects where the
 * resolved entity actually lives. `priority` is the interview priority. */
export interface ResolvedSpec {
  id: number
  key: string
  prompt: string | null
  guidance: string | null
  category: SpecCategory | null
  eligible_respondent_roles: string[]
  priority: SpecPriority | null
  required: boolean
  order: number
  owner_level: OwnerLevel
  membership_id: number | null
  name: string | null
  spec_type: SpecType | null
  unit: string | null
  category_options: CategoryOption[] | null
}

export interface ResolvedSection {
  id: string
  key: string
  title: string
  order: number
  description: string | null
  writing_agent_instructions: string | null
  owner_level: OwnerLevel
  template_id: string | null
  questions: ResolvedSpec[]
  writing_skill: string | null
  skill_config: Record<string, unknown> | null
  depends_on: string[] | null
  depends_on_all: boolean | null
}

export interface ResolvedTemplate {
  id: string
  key: string
  name: string
  description: string | null
  writing_agent_instructions: string | null
  owner_level: OwnerLevel
  root_id: string | null
  sections: ResolvedSection[]
}

export const OWNER_LEVEL_BADGE: Record<
  OwnerLevel,
  { label: string; variant: 'info' | 'warning' | 'success' | 'default' }
> = {
  system: { label: 'System', variant: 'info' },
  office: { label: 'Office', variant: 'warning' },
  program_manager: { label: 'Personal', variant: 'default' },
  program: { label: 'This program', variant: 'success' },
}

export const ROLE_OPTIONS = [
  { value: 'pm', label: 'Program Manager' },
  { value: 'mission_owner', label: 'Mission Owner' },
  { value: 'end_user', label: 'End User' },
  { value: 'technical_sme', label: 'Technical SME' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'legal', label: 'Legal' },
  { value: 'contracting', label: 'Contracting Officer' },
  { value: 'observer', label: 'Observer' },
]

export const CATEGORY_OPTIONS: { value: SpecCategory; label: string }[] = [
  { value: 'problem', label: 'Problem' },
  { value: 'environment', label: 'Environment' },
  { value: 'constraint', label: 'Constraint' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'technical', label: 'Technical' },
  { value: 'operational', label: 'Operational' },
  { value: 'budget', label: 'Budget' },
  { value: 'classification', label: 'Classification' },
]

export const PRIORITY_OPTIONS: { value: SpecPriority; label: string }[] = [
  { value: 'essential', label: 'Essential' },
  { value: 'standard', label: 'Standard' },
  { value: 'optional', label: 'Optional' },
]

export const SPEC_TYPE_OPTIONS: { value: SpecType; label: string }[] = [
  { value: 'text', label: 'Free text' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'categorical', label: 'Categorical' },
  { value: 'ordered_categorical', label: 'Ordered categorical' },
  { value: 'datetime', label: 'Date / time' },
]
